<template>
  <v-sheet class="editor">
    <v-sheet class="menubar mb-0" :color="editorBaseColor">
      <div class="toolbar">
        <v-btn
          dark
          class="mr-1"
          @click="
            editor
              .chain()
              .focus()
              .undo()
              .run()
          "
          :color="editorBaseColor"
          fab
          x-small
          depressed
        >
          <v-icon>mdi-undo</v-icon>
        </v-btn>

        <v-btn
          dark
          class="mr-1"
          @click="
            editor
              .chain()
              .focus()
              .redo()
              .run()
          "
          :color="editorBaseColor"
          fab
          x-small
          depressed
        >
          <v-icon>mdi-redo</v-icon>
        </v-btn>

        <v-btn
          dark
          class="mr-1"
          @click="
            editor
              .chain()
              .focus()
              .toggleBold()
              .run()
          "
          :color="editor.isActive('bold') ? editorSelectingColor : editorBaseColor"
          fab
          x-small
          depressed
        >
          <v-icon>mdi-format-bold</v-icon>
        </v-btn>

        <v-btn
          dark
          class="mr-1"
          :color="editor.isActive('italic') ? editorSelectingColor : editorBaseColor"
          @click="
            editor
              .chain()
              .focus()
              .toggleItalic()
              .run()
          "
          fab
          x-small
          depressed
        >
          <v-icon>mdi-format-italic</v-icon>
        </v-btn>

        <v-btn
          dark
          class="mr-1"
          :color="editor.isActive('strike') ? editorSelectingColor : editorBaseColor"
          @click="
            editor
              .chain()
              .focus()
              .toggleStrike()
              .run()
          "
          fab
          x-small
          depressed
        >
          <v-icon>mdi-format-strikethrough-variant</v-icon>
        </v-btn>

        <v-btn
          dark
          class="mr-1"
          :color="editor.isActive('underline') ? editorSelectingColor : editorBaseColor"
          @click="
            editor
              .chain()
              .focus()
              .toggleUnderline()
              .run()
          "
          fab
          x-small
          depressed
        >
          <v-icon>mdi-format-underline</v-icon>
        </v-btn>

        <v-menu offset-y :nudge-bottom="3" left :nudge-right="44" rounded>
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-on="on" v-bind="attrs" dark class="mr-1" :color="editor.isActive('textAlign') ? editorSelectingColor : editorBaseColor" fab x-small depressed>
              <v-icon>mdi-format-text</v-icon>
            </v-btn>
          </template>
          <div class="d-flex justify-center align-center px-1 py-1 pb-2">
            <v-btn
              dark
              class="mr-1"
              :color="editor.isActive({ textAlign: 'left' }) ? editorSelectingColor : editorBaseColor"
              @click="
                editor
                  .chain()
                  .focus()
                  .setTextAlign('left')
                  .run()
              "
              fab
              x-small
              depressed
            >
              <v-icon>mdi-format-align-left</v-icon>
            </v-btn>
            <v-btn
              dark
              class="mr-1"
              :color="editor.isActive({ textAlign: 'center' }) ? editorSelectingColor : editorBaseColor"
              @click="
                editor
                  .chain()
                  .focus()
                  .setTextAlign('center')
                  .run()
              "
              fab
              x-small
              depressed
            >
              <v-icon>mdi-format-align-center</v-icon>
            </v-btn>
            <v-btn
              dark
              class="mr-1"
              :color="editor.isActive({ textAlign: 'right' }) ? editorSelectingColor : editorBaseColor"
              @click="
                editor
                  .chain()
                  .focus()
                  .setTextAlign('right')
                  .run()
              "
              fab
              x-small
              depressed
            >
              <v-icon>mdi-format-align-right</v-icon>
            </v-btn>
          </div>
        </v-menu>

        <v-btn
          dark
          class="mr-1"
          :color="editor.isActive('code') ? editorSelectingColor : editorBaseColor"
          @click="
            editor
              .chain()
              .focus()
              .toggleCode()
              .run()
          "
          fab
          x-small
          depressed
        >
          <v-icon>mdi-code-tags</v-icon>
        </v-btn>

        <v-btn
          dark
          class="mr-1"
          :color="editor.isActive('codeBlock') ? editorSelectingColor : editorBaseColor"
          @click="
            editor
              .chain()
              .focus()
              .toggleCodeBlock()
              .run()
          "
          fab
          x-small
          depressed
        >
          <v-icon>mdi-code-block-braces</v-icon>
        </v-btn>

        <v-btn dark class="mr-1" @click="setLink" :color="editorBaseColor" fab x-small depressed>
          <v-icon>mdi-link-variant-plus</v-icon>
        </v-btn>

        <v-btn
          dark
          class="mr-1"
          @click="
            editor
              .chain()
              .focus()
              .unsetLink()
              .run()
          "
          v-if="editor.isActive('link')"
          fab
          x-small
          depressed
          :color="editorBaseColor"
        >
          <v-icon>mdi-link-variant-remove</v-icon>
        </v-btn>

        <v-btn dark class="mr-1" :color="editor.isActive('paragraph') ? editorSelectingColor : editorBaseColor" fab x-small depressed>
          <v-icon>mdi-format-paragraph</v-icon>
        </v-btn>

        <v-menu offset-y :nudge-bottom="10">
          <template v-slot:activator="{ on, attrs }">
            <v-btn dark v-on="on" v-bind="attrs" class="editor-heading mr-1" :class="editor.isActive('heading') ? 'pr-2' : ''" :color="editorBaseColor" rounded depressed>
              <span :class="editor.isActive('heading') ? 'mr-2' : ''">{{ $t("PANEL.CHEETAH_EDITOR.HEADING") }}</span>
              <v-chip v-show="editor.isActive('heading', { level: 1 })" color="white" :class="editorSelectingColor" small>
                <v-icon size="18" :color="editorSelectingColor" class="mx-2">mdi-format-header-1</v-icon>
              </v-chip>
              <v-chip v-show="editor.isActive('heading', { level: 2 })" color="white" :class="editorSelectingColor" small>
                <v-icon size="18" :color="editorSelectingColor" class="mx-2">mdi-format-header-2</v-icon>
              </v-chip>
              <v-chip v-show="editor.isActive('heading', { level: 3 })" color="white" :class="editorSelectingColor" small>
                <v-icon size="18" :color="editorSelectingColor" class="mx-2">mdi-format-header-3</v-icon>
              </v-chip>
              <v-chip v-show="editor.isActive('heading', { level: 4 })" color="white" :class="editorSelectingColor" small>
                <v-icon size="18" :color="editorSelectingColor" class="mx-2">mdi-format-header-4</v-icon>
              </v-chip>
              <v-chip v-show="editor.isActive('heading', { level: 5 })" color="white" :class="editorSelectingColor" small>
                <v-icon size="18" :color="editorSelectingColor" class="mx-2">mdi-format-header-5</v-icon>
              </v-chip>
              <v-chip v-show="editor.isActive('heading', { level: 6 })" color="white" :class="editorSelectingColor" small>
                <v-icon size="18" :color="editorSelectingColor" class="mx-2">mdi-format-header-6</v-icon>
              </v-chip>
            </v-btn>
          </template>
          <v-card>
            <v-btn
              :text="!editor.isActive('heading', { level: 1 })"
              :dark="editor.isActive('heading', { level: 1 })"
              class="d-flex justift-content-start mr-1 rounded-none"
              :color="editorBaseColor"
              @click="
                editor
                  .chain()
                  .focus()
                  .toggleHeading({ level: 1 })
                  .run()
              "
              width="100%"
            >
              <span class="text-capitalize">{{ $t("PANEL.CHEETAH_EDITOR.HEAD", { NUMBER: "1" }) }}</span>
            </v-btn>
            <v-btn
              :text="!editor.isActive('heading', { level: 2 })"
              :dark="editor.isActive('heading', { level: 2 })"
              class="d-flex justift-content-start mr-1 rounded-none"
              :color="editorBaseColor"
              @click="
                editor
                  .chain()
                  .focus()
                  .toggleHeading({ level: 2 })
                  .run()
              "
              width="100%"
            >
              <span class="text-capitalize">{{ $t("PANEL.CHEETAH_EDITOR.HEAD", { NUMBER: "2" }) }}</span>
            </v-btn>
            <v-btn
              :text="!editor.isActive('heading', { level: 3 })"
              :dark="editor.isActive('heading', { level: 3 })"
              class="d-flex justift-content-start mr-1 rounded-none"
              :color="editorBaseColor"
              @click="
                editor
                  .chain()
                  .focus()
                  .toggleHeading({ level: 3 })
                  .run()
              "
              width="100%"
            >
              <span class="text-capitalize">{{ $t("PANEL.CHEETAH_EDITOR.HEAD", { NUMBER: "3" }) }}</span>
            </v-btn>
            <v-btn
              :text="!editor.isActive('heading', { level: 4 })"
              :dark="editor.isActive('heading', { level: 4 })"
              class="d-flex justift-content-start mr-1 rounded-none"
              :color="editorBaseColor"
              @click="
                editor
                  .chain()
                  .focus()
                  .toggleHeading({ level: 4 })
                  .run()
              "
              width="100%"
            >
              <span class="text-capitalize">{{ $t("PANEL.CHEETAH_EDITOR.HEAD", { NUMBER: "4" }) }}</span>
            </v-btn>
            <v-btn
              :text="!editor.isActive('heading', { level: 5 })"
              :dark="editor.isActive('heading', { level: 5 })"
              class="d-flex justift-content-start mr-1 rounded-none"
              :color="editorBaseColor"
              @click="
                editor
                  .chain()
                  .focus()
                  .toggleHeading({ level: 5 })
                  .run()
              "
              width="100%"
            >
              <span class="text-capitalize">{{ $t("PANEL.CHEETAH_EDITOR.HEAD", { NUMBER: "5" }) }}</span>
            </v-btn>
            <v-btn
              :text="!editor.isActive('heading', { level: 6 })"
              :dark="editor.isActive('heading', { level: 6 })"
              class="d-flex justift-content-start mr-1 rounded-none"
              :color="editorBaseColor"
              @click="
                editor
                  .chain()
                  .focus()
                  .toggleHeading({ level: 6 })
                  .run()
              "
              width="100%"
            >
              <span class="text-capitalize">{{ $t("PANEL.CHEETAH_EDITOR.HEAD", { NUMBER: "6" }) }}</span>
            </v-btn>
          </v-card>
        </v-menu>

        <v-btn
          dark
          class="mr-1"
          :color="editor.isActive('bulletList') ? editorSelectingColor : editorBaseColor"
          @click="
            editor
              .chain()
              .focus()
              .toggleBulletList()
              .run()
          "
          fab
          x-small
          depressed
        >
          <v-icon>mdi-format-list-bulleted</v-icon>
        </v-btn>

        <v-btn
          dark
          class="mr-1"
          :color="editor.isActive('orderedList') ? editorSelectingColor : editorBaseColor"
          @click="
            editor
              .chain()
              .focus()
              .toggleOrderedList()
              .run()
          "
          fab
          x-small
          depressed
        >
          <v-icon>mdi-format-list-numbered</v-icon>
        </v-btn>

        <v-btn
          dark
          class="mr-1"
          @click="
            editor
              .chain()
              .focus()
              .toggleBlockquote()
              .run()
          "
          :color="editor.isActive('blockquote') ? editorSelectingColor : editorBaseColor"
          fab
          x-small
          depressed
        >
          <v-icon>mdi-format-quote-close</v-icon>
        </v-btn>

        <v-btn dark class="mr-1" @click="imageCommand()" :color="editorBaseColor" fab x-small depressed>
          <v-icon>mdi-image-area</v-icon>
        </v-btn>

        <v-btn
          dark
          class="mr-1"
          @click="
            editor
              .chain()
              .focus()
              .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
              .run()
          "
          :color="editorBaseColor"
          fab
          x-small
          depressed
        >
          <v-icon>mdi-table</v-icon>
        </v-btn>

        <span v-if="editor.isActive('table')">
          <v-btn
            dark
            class="mr-1"
            @click="
              editor
                .chain()
                .focus()
                .deleteTable()
                .run()
            "
            :color="editorBaseColor"
            fab
            x-small
            depressed
          >
            <v-icon>mdi-table-remove</v-icon>
          </v-btn>
          <v-btn
            dark
            class="mr-1"
            @click="
              editor
                .chain()
                .focus()
                .addColumnBefore()
                .run()
            "
            :color="editorBaseColor"
            fab
            x-small
            depressed
          >
            <v-icon>mdi-table-column-plus-after</v-icon>
          </v-btn>
          <v-btn
            dark
            class="mr-1"
            @click="
              editor
                .chain()
                .focus()
                .addColumnAfter()
                .run()
            "
            :color="editorBaseColor"
            fab
            x-small
            depressed
          >
            <v-icon>mdi-table-column-plus-before</v-icon>
          </v-btn>
          <v-btn
            dark
            class="mr-1"
            @click="
              editor
                .chain()
                .focus()
                .deleteColumn()
                .run()
            "
            :color="editorBaseColor"
            fab
            x-small
            depressed
          >
            <v-icon>mdi-table-column-remove</v-icon>
          </v-btn>
          <v-btn
            dark
            class="mr-1"
            @click="
              editor
                .chain()
                .focus()
                .addRowBefore()
                .run()
            "
            :color="editorBaseColor"
            fab
            x-small
            depressed
          >
            <v-icon>mdi-table-row-plus-before</v-icon>
          </v-btn>
          <v-btn
            dark
            class="mr-1"
            @click="
              editor
                .chain()
                .focus()
                .addRowAfter()
                .run()
            "
            :color="editorBaseColor"
            fab
            x-small
            depressed
          >
            <v-icon>mdi-table-row-plus-after</v-icon>
          </v-btn>
          <v-btn
            dark
            class="mr-1"
            @click="
              editor
                .chain()
                .focus()
                .deleteRow()
                .run()
            "
            :color="editorBaseColor"
            fab
            x-small
            depressed
          >
            <v-icon>mdi-table-row-remove</v-icon>
          </v-btn>
          <v-btn
            dark
            class="mr-1"
            @click="
              editor
                .chain()
                .focus()
                .mergeCells()
                .run()
            "
            :color="editorBaseColor"
            fab
            x-small
            depressed
          >
            <v-icon>mdi-table-merge-cells</v-icon>
          </v-btn>
        </span>
      </div>
    </v-sheet>
    <!-- <bubble-menu :editor="editor">
    </bubble-menu>-->
    <!-- 
      <bubble-menu
        class="tooltip richtext-link"
      >
      afasfdaf
    </bubble-menu>-->
    <editor-content ref="editor" class="editor__content" :class="editorPassiveColor" :style="`border-color:${editorBorderColor} !important;`" :editor="editor" />
    <media-explorer v-model="explorer" @selected="addCommand" format="images" :list-selection="false" :multiple="true" />
  </v-sheet>
</template>

<script>
import { Editor, EditorContent } from "@tiptap/vue-2";

import Blockquote from "@tiptap/extension-blockquote";
import Underline from "@tiptap/extension-underline";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import StarterKit from "@tiptap/starter-kit";
import CodeBlock from "@tiptap/extension-code-block";

import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";

import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import ListItem from "@tiptap/extension-list-item";

import { mergeAttributes } from "@tiptap/vue-2";

import MediaExplorer from "./Settings/Media/MediaExplorer.vue";

const parse_BR_bug = htmlContent => {
  return htmlContent.replace(/<br[^>]*>/g, function(match) {
    // Eğer ProseMirror-trailingBreak sınıfı yoksa, etiketi kaldırın
    if (!/ProseMirror-trailingBreak/.test(match)) {
      return "";
    }
    // ProseMirror-trailingBreak sınıfı varsa, etiketi değiştirmeden bırakın
    return match;
  });
};

// import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
// import css from "highlight.js/lib/languages/css";
// import js from "highlight.js/lib/languages/javascript";
// import ts from "highlight.js/lib/languages/typescript";
// import html from "highlight.js/lib/languages/xml";
// import { lowlight } from "lowlight";

// lowlight.registerLanguage("html", html);
// lowlight.registerLanguage("css", css);
// lowlight.registerLanguage("js", js);
// lowlight.registerLanguage("ts", ts);

export const MyImage = Image.extend({
  defaultOptions: {
    ...Image.options,
    sizes: ["inline", "block", "left", "right"]
  },
  renderHTML({ HTMLAttributes }) {
    const { style } = HTMLAttributes;
    return [
      "figure",
      { style },
      ["img", mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)]
    ];
  }
})

export default {
  props: {
    minHeight: {
      default: undefined
    },
    value: {
      default: ""
    },
    setContent: {
      default: ""
    },
    editorBaseColor: {
      default: "orange darken-3"
    },
    editorSelectingColor: {
      default: "orange darken-4"
    },
    editorPassiveColor: {
      default: "orange lighten-5"
    },
    editorBorderColor: {
      default: "#FFB74D"
    }
  },
  components: {
    EditorContent,
    // BubbleMenu,
    MediaExplorer
  },
  data() {
    return {
      content: "",
      editor: new Editor({
        extensions: [
          // BubbleMenu.configure({
          //   element: document.querySelector(".menu")
          // }),
          Document,
          Paragraph,
          Text,
          StarterKit,
          TextAlign.configure({
            types: ["heading", "paragraph", "image"]
          }),
          Link.configure({
            openOnClick: false
          }),
          Blockquote,
          Underline,
          MyImage.configure({
            HTMLAttributes: {
              class: 'post-box--image'
            },
            allowBase64: true,
          }),
          Table.configure({
            resizable: true
          }),
          TableRow,
          TableCell,
          TableHeader,
          BulletList,
          OrderedList,
          ListItem,
          CodeBlock.configure({
            languageClassPrefix: "language-"
          })
          // CodeBlockLowlight.configure({
          //   languageClassPrefix: "language-",
          //   lowlight
          // })
        ],
        content: ``,
        onCreate: ({ editor }) => {
          this.$emit("input", parse_BR_bug(editor.view.dom.innerHTML));
          this.$emit("firstContent", parse_BR_bug(editor.view.dom.innerHTML));
        },
        onUpdate: ({ editor }) => {
          // get new content on update
          // this.content = getHTML();
          this.$emit("input", parse_BR_bug(editor.view.dom.innerHTML));
        }
      }),
      explorer: false,
      command: ""
    };
  },
  methods: {
    imageCommand() {
      this.explorer = true;
    },
    addCommand(images) {
      images.forEach(image => {
        this.editor
          .chain()
          .focus()
          .setImage({ src: `${this.$apiImagesUrl}/${image.name}` })
          .run();
        // alt: "YOU CAN ADD ALT",
        // title: "YOU CAN ADD TITLE"
      });
    },
    setLink() {
      const url = window.prompt("URL");

      this.editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: url })
        .run();
    },
    unsetLink(command) {
      command({ href: false });
    },
    updateContent(e) {
      console.log("updated:", e);
    }
  },
  mounted() {
    // let editorHTML = this.$refs.editor.$el
    // .preventDefault()
  },
  watch: {
    content(data) {
      this.$emit("input", data);
    },
    setContent(value) {
      this.editor.commands.setContent(value);
    }
  },
  beforeDestroy() {
    this.editor.destroy();
  }
};
</script>

<style scoped>
.tooltip {
  position: relative;
}
</style>

<style>
.editor * {
  margin: 0;
  padding: 0;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  -webkit-text-size-adjust: 100%;
  -moz-text-size-adjust: 100%;
  text-size-adjust: 100%;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  -webkit-touch-callout: none;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
}
.editor :focus {
  outline: none;
}
.editor :after,
.editor :before {
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
}
.editor html {
  font-family: Inter;
  font-size: 18px;
  color: #000;
  line-height: 1.5;
}
.editor body {
  margin: 0;
}
.editor a {
  color: inherit;
}
.editor blockquote,
.editor h1,
.editor h2,
.editor h3,
.editor ol,
.editor p,
.editor pre,
.editor ul {
  margin: 0.25rem 0;
}
.editor blockquote:first-child,
.editor h1:first-child,
.editor h2:first-child,
.editor h3:first-child,
.editor ol:first-child,
.editor p:first-child,
.editor pre:first-child,
.editor ul:first-child {
  margin-top: 0;
}
.editor blockquote:last-child,
.editor h1:last-child,
.editor h2:last-child,
.editor h3:last-child,
.editor ol:last-child,
.editor p:last-child,
.editor pre:last-child,
.editor ul:last-child {
  margin-bottom: 0;
}
.editor h1,
.editor h2,
.editor h3 {
  line-height: 1.3;
}
.button {
  font-weight: 700;
  display: -webkit-inline-box;
  display: inline-flex;
  background: transparent;
  border: 0;
  color: #000;
  padding: 0.2rem 0.5rem;
  margin-right: 0.2rem;
  border-radius: 3px;
  cursor: pointer;
  background-color: rgba(0, 0, 0, 0.1);
}
.button:hover {
  background-color: rgba(0, 0, 0, 0.15);
}
.message {
  background-color: rgba(0, 0, 0, 0.05);
  color: rgba(0, 0, 0, 0.7);
  padding: 1rem;
  border-radius: 6px;
  margin-bottom: 1.5rem;
  font-style: italic;
}
.editor {
  position: relative;
}
.editor__content {
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
  border: 1px solid rgba(0, 0, 0, 0.38);
  border-radius: 0 0 6px 6px;
  padding: 0.35em;
}
.editor__content > .ProseMirror {
  min-height: 150px;
}
.editor__content * {
  caret-color: currentColor;
}
.editor__content pre {
  padding: 0.7rem 1rem;
  border-radius: 5px;
  background: #000;
  color: #fff;
  font-size: 0.8rem;
  overflow-x: auto;
}
.editor__content pre code {
  display: block;
  background-color: transparent;
  color: #fff;
  font-size: 0.9rem;
  font-weight: 500;
}
.editor__content p code {
  padding: 0.2rem 0.4rem;
  border-radius: 5px;
  font-size: 0.8rem;
  font-weight: 700;
  background: rgba(0, 0, 0, 0.1);
  color: rgba(0, 0, 0, 0.8);
}
.editor__content ol,
.editor__content ul {
  padding-left: 1rem;
}
.editor__content li > ol,
.editor__content li > p,
.editor__content li > ul {
  margin: 0;
}
.editor__content a {
  color: inherit;
}
.editor__content blockquote {
  border-left: 3px solid rgba(0, 0, 0, 0.1);
  color: rgba(0, 0, 0, 0.8);
  padding-left: 0.8rem;
  font-style: italic;
}
.editor__content blockquote p {
  margin: 0;
}
.editor__content img {
  max-width: 100%;
  border-radius: 3px;
}
.editor__content table {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
  margin: 0;
  overflow: hidden;
}
.editor__content table td,
.editor__content table th {
  min-width: 1em;
  border: 2px solid #ddd;
  padding: 3px 5px;
  vertical-align: top;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  position: relative;
}
.editor__content table td > *,
.editor__content table th > * {
  margin-bottom: 0;
}
.editor__content table th {
  font-weight: 700;
  text-align: left;
}
.editor__content table .selectedCell:after {
  z-index: 2;
  position: absolute;
  content: "";
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: rgba(200, 200, 255, 0.4);
  pointer-events: none;
}
.editor__content table .column-resize-handle {
  position: absolute;
  right: -2px;
  top: 0;
  bottom: 0;
  width: 4px;
  z-index: 20;
  background-color: #adf;
  pointer-events: none;
}
.editor__content .tableWrapper {
  margin: 1em 0;
  overflow-x: auto;
}
.editor__content .resize-cursor {
  cursor: ew-resize;
  cursor: col-resize;
}
.editor .menubar {
  padding: 8px;
  border-radius: 5px 5px 0 0;
  -webkit-transition: visibility 0.2s 0.4s, opacity 0.2s 0.4s;
  transition: visibility 0.2s 0.4s, opacity 0.2s 0.4s;
}
.editor .menubar.is-hidden {
  visibility: hidden;
  opacity: 0;
}
.editor .menubar.is-focused {
  visibility: visible;
  opacity: 1;
  -webkit-transition: visibility 0.2s, opacity 0.2s;
  transition: visibility 0.2s, opacity 0.2s;
}
.editor .menubar__button {
  font-weight: 700;
  display: -webkit-inline-box;
  display: inline-flex;
  background: transparent;
  border: 0;
  color: #000;
  padding: 0.2rem 0.5rem;
  margin-right: 0.2rem;
  border-radius: 3px;
  cursor: pointer;
}
.editor .menubar__button:hover {
  background-color: rgba(0, 0, 0, 0.05);
}
.editor .menubar__button.is-active {
  background-color: rgba(0, 0, 0, 0.1);
}
.editor .menubar span.menubar__button {
  font-size: 13.3333px;
}
.editor .menububble {
  position: absolute;
  display: -webkit-box;
  display: flex;
  z-index: 20;
  background: #000;
  border-radius: 5px;
  padding: 0.3rem;
  margin-bottom: 0.5rem;
  -webkit-transform: translateX(-50%);
  transform: translateX(-50%);
  visibility: hidden;
  opacity: 0;
  -webkit-transition: opacity 0.2s, visibility 0.2s;
  transition: opacity 0.2s, visibility 0.2s;
}
.editor .menububble.is-active {
  opacity: 1;
  visibility: visible;
}
.editor .menububble__button {
  display: -webkit-inline-box;
  display: inline-flex;
  background: transparent;
  border: 0;
  color: #fff;
  padding: 0.2rem 0.5rem;
  margin-right: 0.2rem;
  border-radius: 3px;
  cursor: pointer;
}
.editor .menububble__button:last-child {
  margin-right: 0;
}
.editor .menububble__button:hover {
  background-color: hsla(0, 0%, 100%, 0.1);
}
.editor .menububble__button.is-active {
  background-color: hsla(0, 0%, 100%, 0.2);
}
.editor .menububble__form {
  display: -webkit-box;
  display: flex;
  -webkit-box-align: center;
  align-items: center;
}
.editor .menububble__input {
  font: inherit;
  border: none;
  background: transparent;
  color: #fff;
}
.editor .banner {
  font-size: 0.9rem;
  background-color: #fff;
  color: #000;
  border-bottom: 1px solid #ddd;
  text-align: center;
  padding: 0.5rem 1rem;
}
.editor .banner__action {
  display: inline-block;
  border: 3px solid #ddd;
  font-weight: 700;
  -webkit-transition: all 0.1s ease-in-out;
  transition: all 0.1s ease-in-out;
  text-decoration: none;
  padding: 0.4rem 0.7rem;
  margin: 0.5rem 0.5rem 0.25rem;
  border-radius: 5px;
  white-space: nowrap;
}
.editor .banner__action:hover {
  border-color: #000;
}
@media (max-width: 600px) {
  .editor .banner__message {
    display: block;
  }
}
.editor .icon {
  position: relative;
  display: inline-block;
  vertical-align: middle;
  width: 0.8rem;
  height: 1.25rem;
  margin: 0 0.3rem;
  top: -0.05rem;
  fill: currentColor;
}
.editor .icon__svg {
  display: inline-block;
  vertical-align: top;
  width: 100%;
  height: 100%;
}
.editor .icon:first-child {
  margin-left: 0;
}
.editor .icon:last-child {
  margin-right: 0;
}
.editor .icon use > svg circle,
.editor .icon use > svg g,
.editor .icon use > svg path,
.editor .icon use > svg rect,
.editor body > svg circle,
.editor body > svg g,
.editor body > svg path,
.editor body > svg rect,
.editor symbol circle,
.editor symbol g,
.editor symbol path,
.editor symbol rect {
  fill: currentColor;
  stroke: none;
}
.editor .icon use > svg [d="M0 0h24v24H0z"],
body > svg [d="M0 0h24v24H0z"],
symbol [d="M0 0h24v24H0z"] {
  display: none;
}
.editor .navigation[data-v-802d4490] {
  display: -webkit-box;
  display: flex;
  -webkit-box-pack: justify;
  justify-content: space-between;
  -webkit-box-align: center;
  align-items: center;
  padding: 0.75rem;
  background-color: #000;
  color: #fff;
  flex-wrap: wrap;
}
.editor .navigation__logo[data-v-802d4490] {
  display: inline-block;
  vertical-align: middle;
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0 0.5rem 0 0;
}
.editor .navigation__icon[data-v-802d4490] {
  width: 1.5rem;
  height: 1.5rem;
}
.editor .navigation__count[data-v-802d4490] {
  display: inline-block;
  vertical-align: middle;
  margin-top: 0.3rem;
}
.editor .navigation__link[data-v-802d4490] {
  display: inline-block;
  color: hsla(0, 0%, 100%, 0.5);
  text-decoration: none;
  font-weight: 700;
  font-size: 0.9rem;
  padding: 0.1rem 0.5rem;
  border-radius: 3px;
}
.editor .navigation__link[data-v-802d4490]:hover {
  color: #fff;
  background-color: hsla(0, 0%, 100%, 0.1);
}
.editor .navigation__github-link[data-v-802d4490] {
  margin-left: 0.5rem;
}
.editor .hero[data-v-5a80d244] {
  background-color: #000;
  color: #fff;
  text-align: center;
  padding: 3rem 1rem;
}
.editor .hero__inner[data-v-5a80d244] {
  margin: 0 auto;
  max-width: 30rem;
}
.editor .hero__logo[data-v-5a80d244] {
  width: 4rem;
  height: 4rem;
}
.editor .hero__logo path[data-v-5a80d244] {
  fill: #fff;
}
.editor .subnavigation[data-v-460c8f74] {
  padding: 0.5rem;
  background-color: rgba(0, 0, 0, 0.9);
  color: #fff;
  text-align: center;
}
@media (min-width: 600px) {
  .editor .subnavigation[data-v-460c8f74] {
    position: -webkit-sticky;
    position: sticky;
    top: 0;
    z-index: 1000;
  }
}
.editor .subnavigation__link[data-v-460c8f74] {
  display: inline-block;
  color: hsla(0, 0%, 100%, 0.5);
  text-decoration: none;
  font-weight: 700;
  font-size: 0.9rem;
  padding: 0.1rem 0.5rem;
  border-radius: 3px;
}
.editor .subnavigation__link[data-v-460c8f74]:hover {
  color: #fff;
  background-color: hsla(0, 0%, 100%, 0.1);
}
.editor .subnavigation__link.is-exact-active[data-v-460c8f74] {
  color: #fff;
  background-color: hsla(0, 0%, 100%, 0.2);
}
.editor .page__content {
  padding: 4rem 1rem;
}
.editor .page__footer {
  text-align: center;
  margin-bottom: 2rem;
}
.editor .page__source-link {
  display: inline-block;
  text-decoration: none;
  text-transform: uppercase;
  font-weight: 700;
  font-size: 0.8rem;
  background-color: rgba(0, 0, 0, 0.1);
  color: #000;
  border-radius: 5px;
  padding: 0.2rem 0.5rem;
}
.editor-heading .v-btn__content {
  font-size: 12px;
}
.ProseMirror p {
  margin-bottom: 0.5em;
}
p br:not(.ProseMirror-trailingBreak) {
  display: none;
}
.menubar {
  position: sticky;
  top: -2px;
  z-index: 3;
}
</style>

<style lang="scss">
/* Image Extension Styles */

.tiptap img {
  border: 2px dashed transparent;
}

.ProseMirror-selectednode img {
  border-color: #111;
}
</style>
